// (c) 2026 Planet Q Productions. All Rights Reserved.

import React, { useState, useEffect } from 'react';
import SharePanel from './SharePanel';

const PostGameSummary = ({ score, gameTitle, gameMode, screenshotDataUrl, onPlayAgain, onBackToMenu, isVictory }) => {
  const [showStats, setShowStats] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [animFrame, setAnimFrame] = useState(0);

  const modeLabel = {
    run: 'Runner', fly: 'Flight', combat: 'Combat',
    race: 'Racer', openworld: 'Open World', puzzle: 'Puzzler',
  }[gameMode] || 'Game';

  // Staggered reveal animations
  useEffect(() => {
    const t1 = setTimeout(() => setShowStats(true), 400);
    const t2 = setTimeout(() => setShowShare(true), 1000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Particle animation
  useEffect(() => {
    const id = setInterval(() => setAnimFrame(f => f + 1), 50);
    return () => clearInterval(id);
  }, []);

  const getRank = () => {
    if (score >= 5000) return { rank: 'S', color: '#FFD700', label: 'LEGENDARY' };
    if (score >= 3000) return { rank: 'A', color: '#FF69B4', label: 'EXCELLENT' };
    if (score >= 1500) return { rank: 'B', color: '#00F0FF', label: 'GREAT' };
    if (score >= 500) return { rank: 'C', color: '#00FF88', label: 'GOOD' };
    return { rank: 'D', color: '#FF8844', label: 'KEEP TRYING' };
  };

  const { rank, color, label } = getRank();
  const pulse = Math.sin(animFrame * 0.08) * 0.15 + 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,5,15,0.95)' }} data-testid="post-game-summary">
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 30 }).map((_, i) => {
          const x = ((i * 37 + animFrame * 0.3) % 100);
          const y = ((i * 53 + animFrame * 0.2) % 100);
          const s = 1 + (i % 3);
          const o = 0.1 + (i % 5) * 0.05;
          return (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                left: `${x}%`, top: `${y}%`,
                width: s, height: s,
                background: isVictory ? '#FFD700' : '#00F0FF',
                opacity: o,
              }}
            />
          );
        })}
      </div>

      <div className="relative z-10 text-center max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
        {/* Header */}
        <div className="mb-4">
          {isVictory ? (
            <h1
              className="text-5xl font-bold mb-1"
              style={{ fontFamily: "'Rajdhani', sans-serif", color: '#FFD700', textShadow: '0 0 30px rgba(255,215,0,0.4)', transform: `scale(${pulse})` }}
              data-testid="victory-title"
            >
              VICTORY
            </h1>
          ) : (
            <h1
              className="text-5xl font-bold mb-1"
              style={{ fontFamily: "'Rajdhani', sans-serif", color: '#FF4444', textShadow: '0 0 30px rgba(255,68,68,0.4)' }}
              data-testid="game-over-title"
            >
              GAME OVER
            </h1>
          )}
          <p className="text-cyan-400/60 text-sm uppercase tracking-[0.3em]" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            {gameTitle || 'PLANET Q'} — {modeLabel} Mode
          </p>
        </div>

        {/* Score + Rank Card */}
        {showStats && (
          <div
            className="mb-4 p-5 rounded-lg transition-all duration-700"
            style={{
              background: 'rgba(0,20,40,0.6)',
              border: `1px solid ${color}30`,
              boxShadow: `0 0 30px ${color}10`,
              opacity: showStats ? 1 : 0,
              transform: showStats ? 'translateY(0)' : 'translateY(20px)',
            }}
            data-testid="score-card"
          >
            <p className="text-white/40 text-xs uppercase tracking-widest mb-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>Final Score</p>
            <p className="text-4xl font-bold mb-2" style={{ fontFamily: "'Rajdhani', sans-serif", color }} data-testid="final-score">
              {score.toLocaleString()}
            </p>

            {/* Rank Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full" style={{ background: `${color}15`, border: `1px solid ${color}40` }}>
              <span className="text-2xl font-black" style={{ color, fontFamily: "'Rajdhani', sans-serif" }} data-testid="rank-badge">{rank}</span>
              <span className="text-xs uppercase tracking-widest" style={{ color: `${color}CC`, fontFamily: "'Rajdhani', sans-serif" }}>{label}</span>
            </div>

            {/* Screenshot preview */}
            {screenshotDataUrl && (
              <div className="mt-3 rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                <img src={screenshotDataUrl} alt="Game screenshot" className="w-full h-auto" style={{ maxHeight: 160, objectFit: 'cover' }} />
              </div>
            )}
          </div>
        )}

        {/* Share Section */}
        {showShare && (
          <div
            className="transition-all duration-700"
            style={{ opacity: showShare ? 1 : 0, transform: showShare ? 'translateY(0)' : 'translateY(20px)' }}
          >
            <SharePanel
              score={score}
              gameTitle={gameTitle}
              gameMode={gameMode}
              screenshotDataUrl={screenshotDataUrl}
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-2 items-center mt-5">
          <button
            data-testid="summary-play-again"
            onClick={onPlayAgain}
            className="w-64 py-3 rounded-full text-white font-bold text-base uppercase tracking-wider transition-all hover:scale-105 active:scale-95"
            style={{ background: 'linear-gradient(135deg, #00F0FF 0%, #0088AA 100%)', fontFamily: "'Rajdhani', sans-serif" }}
          >
            PLAY AGAIN
          </button>
          <button
            data-testid="summary-back-to-menu"
            onClick={onBackToMenu}
            className="w-64 py-3 rounded-full text-white font-bold text-base uppercase tracking-wider transition-all hover:scale-105 active:scale-95"
            style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.25)', fontFamily: "'Rajdhani', sans-serif" }}
          >
            BACK TO MENU
          </button>
        </div>

        <p className="text-white/20 text-[10px] mt-4 uppercase tracking-widest" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          Planet Q Productions
        </p>
      </div>
    </div>
  );
};

export default PostGameSummary;
